import React, { useEffect, useState } from "react";
import {
  MdAdd,
  MdDelete,
  MdPeople,
  MdLogin,
} from "react-icons/md";
import toast from "react-hot-toast";
import axiosInstance from "../../utils/api";
import DeleteModal from "../../components/deleteModal";
import Sidebar from "../../components/Sidebar";
import AddUserModal from "../../components/addUserModal";

const defaultActivities = [
  { key: "arrival", name: "Arrival Onsite" },
  { key: "offloading", name: "Material Offloading" },
  { key: "platformBuild", name: "Platform Build" },
  { key: "metalWork", name: "Metal Work" },
  { key: "structureBuild", name: "Structure Build" },
  { key: "painting", name: "Painting" },
  { key: "lighting", name: "Lighting Setup" },
  { key: "avSetup", name: "AV Setup" },
  { key: "graphics", name: "Graphics" },
  { key: "equipmentSetup", name: "Equipment Setup" },
  { key: "avTesting", name: "AV Testing" },
  { key: "equipmentTesting", name: "Equipment Testing" },
  { key: "furnitureSetup", name: "Furniture Setup" },
  { key: "standCleaning", name: "Stand Cleaning" },
];

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    userId: null,
  });
  const [eventsByUser, setEventsByUser] = useState({});

  const [form, setForm] = useState({
    // step 1
    eventName: "",
    name: "",
    email: "",
    phone: "",

    // step 2
    customFields: [],

    // step 3
    activities: defaultActivities,

    //step 4
    dates: {
      buildUp: { start: "", end: "" },
      showDays: { start: "", end: "" },
      teardown: { start: "", end: "" },
    },
    hallsNumber: 0,
    tentsNumber: 0,
    electricity: true,
    internet: true,

  });

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const [usersRes, eventsRes] = await Promise.all([
        axiosInstance.get("/auth/users"),
        axiosInstance.get("/event"),
      ]);

      const usersData = usersRes.data || [];
      const eventsData = eventsRes.data?.data || [];

      const eventsMap = {};
      eventsData.forEach((event) => {
        if (event.user?._id) {
          eventsMap[event.user._id] = event;
        }
      });

      setUsers(usersData);
      setEventsByUser(eventsMap);

    } catch (err) {
      toast.error("Failed to load users or events.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const validateForm = () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return false;
    }

    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(form.email.trim())) {
      toast.error("Please enter a valid email.");
      return false;
    }

    return true;
  };

  const handleLoginAs = async (userId) => {
    try {
      const res = await axiosInstance.post(`/auth/login-as/${userId}`);
      const { accessToken, refreshToken, user } = res.data;

      const adminUrl = new URL("https://app.expo-pilot.com");
      adminUrl.searchParams.set("impersonate", "1");
      adminUrl.searchParams.set("at", accessToken);
      adminUrl.searchParams.set("rt", refreshToken);
      adminUrl.searchParams.set("u", encodeURIComponent(JSON.stringify(user)));

      window.open(adminUrl.toString(), "_blank");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to login as user.");
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const userPayload = {
        name: form.name.trim(),
        email: form.email.trim(),
        role: 'admin',
        ...(form.phone && { phone: form.phone.trim() }),
      };

      const userRes = await axiosInstance.post("/auth/register", userPayload);
      const userId = userRes.data?.user?._id;
      if (!userId) throw new Error("User creation failed");

      const customFieldsOnly = (form.customFields || [])
        .slice(5)
        .filter(field => field.name && field.name.trim() !== "");

      const eventPayload = {
        name: form.eventName,
        user: userId,
        buildUp: form.dates.buildUp,
        showDays: form.dates.showDays,
        teardown: form.dates.teardown,
        customFields: customFieldsOnly,
        hallsNumber: form.hallsNumber,
        tentsNumber: form.tentsNumber,
        electricity: form.electricity,
        internet: form.internet,
      };

      const eventRes = await axiosInstance.post("/event", eventPayload);
      const eventId = eventRes.data?.data?._id;
      if (!eventId) throw new Error("Event creation failed");

      const activityRequests = form.activities.map((activity, index) => {
        const payload = {
          name: activity.name,
          percent: Number(activity.percent),
          event: eventId,
          order: index,
        };
        return axiosInstance.post("/activity", payload);
      });

      await Promise.all(activityRequests);

      toast.success("User, event and activities created successfully");

      setForm({
        name: "",
        email: "",
        phone: "",
        eventName: "",
        customFields: [],
        dates: {
          buildUp: { start: "", end: "" },
          showDays: { start: "", end: "" },
          teardown: { start: "", end: "" },
        },
        activities: defaultActivities,
        hallsNumber: 0,
        tentsNumber: 0,
        electricity: true,
        internet: true,
      });
      setIsCreateModalOpen(false);
      await fetchUsers();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to create user and event"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteModal.userId) return;

    try {
      setSubmitting(true);
      await axiosInstance.delete(`/auth/user/${deleteModal.userId}`);

      toast.success("User account deleted successfully.");
      setDeleteModal({ isOpen: false, userId: null });

      await fetchUsers();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to delete user account."
      );
      setDeleteModal({ isOpen: false, userId: null });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <main className="flex-1 p-8 overflow-y-auto">

        <header className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-gray-900 rounded-xl">
                <MdPeople className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                User Management
              </h1>
            </div>
            <p className="text-gray-600">
              Create and manage user accounts for the admin.
            </p>
          </div>

          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors shadow-sm"
          >
            <MdAdd className="w-5 h-5" />
            Create User
          </button>
        </header>

        <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            {loading && (
              <span className="text-sm text-gray-500">Loading...</span>
            )}
          </div>

          {users.length === 0 && !loading ? (
            <div className="py-16 text-center">
              <MdPeople className="w-14 h-14 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No users found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">

                <thead className="bg-gray-50">
                  <tr>
                    {["Event", "User", "Email", "Phone", "Actions"].map((h) => (
                      <th
                        key={h}
                        className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 bg-white">
                  {users.map((user) => {
                    const id = user._id || user.id;
                    return (
                      <tr
                        key={id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 text-gray-900 font-medium">
                          {eventsByUser[id]?.name || "-"}
                        </td>

                        <td className="px-6 py-4 flex items-center gap-3 text-gray-900">
                          <span className="font-medium">{user.name || "-"}</span>
                        </td>

                        <td className="px-6 py-4 text-gray-600">
                          {user.email || "-"}
                        </td>

                        <td className="px-6 py-4 text-gray-600">
                          {user.phone || "-"}
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleLoginAs(id)}
                              disabled={submitting}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                            >
                              <MdLogin />
                              Login As
                            </button>
                            <button
                              onClick={() => setDeleteModal({ isOpen: true, userId: id })}
                              disabled={submitting}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                            >
                              <MdDelete />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      <AddUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateUser}
        form={form}
        setForm={setForm}
        isSubmitting={submitting}
      />

      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, userId: null })}
        onConfirm={handleDeleteUser}
        title="Delete User Account"
        message="Are you sure you want to delete this user account? This action cannot be undone."
        isDeleting={submitting}
      />
    </div>
  );

};

export default Users;